import {
  Box,
  InputBase,
  MenuItem,
  Popover,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';
import { tokens } from '../../global/theme/tokens';
import ExoAvatar from '../exo/ExoAvatar';

const userName = (user) =>
  `${user?.firstName || ''} ${user?.lastName || ''}`.trim();

/**
 * Small popper to assign employees right where the click happened, instead of
 * crossing the screen to a dialog. Clicking a name toggles the assignment.
 *
 * users: everyone that can be assigned
 * assignedIds: ids of the users already on the task
 */
const AssignUserPopover = ({
  anchorEl,
  onClose,
  users = [],
  assignedIds = [],
  onToggleUser,
  isBusy,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const [search, setSearch] = useState('');

  // the search of the last opening does not apply to the next one
  useEffect(() => {
    if (anchorEl) setSearch('');
  }, [anchorEl]);

  const visibleUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return users;
    return users.filter((user) => userName(user).toLowerCase().includes(term));
  }, [users, search]);

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      // the global backdrop blurs everything behind any modal
      sx={{
        '.MuiBackdrop-root': {
          bgcolor: 'transparent',
          background: 'unset',
          backdropFilter: 'unset',
        },
      }}
      slotProps={{
        paper: {
          sx: {
            minWidth: '220px',
            maxWidth: 'min(280px, calc(100vw - 32px))',
            maxHeight: '320px',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '12px',
            padding: '4px',
            backgroundColor: colors.glassSolid,
            backgroundImage: 'unset',
            border: `1px solid ${colors.text}15`,
            opacity: isBusy ? 0.6 : 1,
            pointerEvents: isBusy ? 'none' : 'auto',
            // the global list styles put a border on every item
            '& .MuiMenuItem-root': {
              border: 'none !important',
              borderRadius: '8px',
              gap: 1,
              paddingY: '3px',
              paddingX: '8px',
              maxWidth: '100%',
            },
          },
        },
      }}
    >
      <Box
        className="flex items-center gap-1 rounded-lg px-2 mb-1 shrink-0"
        sx={{ backgroundColor: colors.bgInput }}
      >
        <SearchIcon sx={{ fontSize: '15px', opacity: 0.5 }} />
        <InputBase
          autoFocus
          fullWidth
          value={search}
          placeholder={t('Search employee...')}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ fontSize: '12px', paddingY: '2px' }}
        />
      </Box>

      <Box className="overflow-y-auto">
        {visibleUsers.length ? (
          visibleUsers.map((user) => {
            const isAssigned = assignedIds.includes(user.id);

            return (
              <MenuItem key={user.id} onClick={() => onToggleUser(user)}>
                <ExoAvatar
                  name={userName(user)}
                  picture={user.picture?.thumbnailUrl}
                  size="22px"
                />
                <Typography
                  fontSize="13px"
                  noWrap
                  className="grow"
                  sx={{ fontWeight: isAssigned ? 600 : 400 }}
                >
                  {userName(user)}
                </Typography>
                <CheckIcon
                  sx={{
                    fontSize: '15px',
                    flexShrink: 0,
                    color: colors.success,
                    opacity: isAssigned ? 1 : 0,
                  }}
                />
              </MenuItem>
            );
          })
        ) : (
          <Typography fontSize="12px" className="opacity-50 text-center py-3">
            {t('No employees found')}
          </Typography>
        )}
      </Box>
    </Popover>
  );
};

export default AssignUserPopover;
